import { AspectRatio } from '@/components/aspect-ratio';
import { CardContent } from '@/components/card';
import { ConditionalRenderer } from '@/components/conditional-renderer';
import { Theme, useTheme } from '@mui/material/styles';
import { rgbToCssColor } from '@/lib/utils/format';
import { useNodeProps } from '@/node-editor/hooks';
import { userHasTicket } from '@/node-editor/utilities';
import { ITextLink } from '@/types/link';
import { INode, INodeProps } from '@/types/node';
import { Box, darken, Icon, Typography } from '@mui/material';
import React, { ReactNode } from 'react';
import { RGBColor } from 'react-color';
import { getImage, ImageSize } from '@shared/utils/imageFormat';
import { IContent } from './contract';
import { useLinkAction } from '@/hooks/useLinkAction';
import { useRowContext } from '@/nodes/row/context';

interface INodeExtended extends Omit<INode, 'content'> {
  content: IContent;
}

interface IProps extends Omit<INodeProps, 'node'> {
  node: INodeExtended;
}

const useStyles = (
  theme: Theme,
  {
    maxWidth,
    minWidth,
    backgroundImage,
    backgroundSize,
    backgroundColor,
    spacing,
    override,
    rowSpacing,
  }: Pick<
    IContent,
    'backgroundImage' | 'backgroundSize' | 'maxWidth' | 'minWidth'
  > & {
    backgroundColor: string;
    spacing: number;
    override: Record<string, any>;
    rowSpacing: number;
  }
) => {
  // Calculate additional spacing from row context
  // Row spacing is in MUI spacing units (8px each), divided by 2 for each side
  const rowSpacingInPx = rowSpacing * 8;

  return {
    root: {
      display: 'flex !important',
      flex: '0 1 auto !important',
      flexDirection: 'column !important',
      borderRadius: '5px !important',
      filter: `drop-shadow(1px 1px 3px rgba(0, 0, 0, 0.3)) !important`,
      width: `calc(${100 / parseInt(minWidth)}% - ${theme.spacing(
        spacing * 2
      )} - ${rowSpacingInPx}px)`,
      [`${theme.breakpoints.up('md')}`]: {
        width: `calc(${100 / parseInt(maxWidth)}% - ${theme.spacing(
          spacing * 2
        )} - ${rowSpacingInPx}px)`,
      },
      margin: `${theme.spacing(spacing)} !important`,
      transition: 'box-shadow .3s linear !important',
      '&:hover, &:focus-within': {
        boxShadow: `0 0 0 2px ${theme.palette.primary.main}`,
      },
      ...override.root,
    },
    aspectContainer: {
      borderRadius: '5px !important',
      backgroundColor: `${backgroundColor} !important`,
      backgroundImage: `${backgroundImage} !important`
        ? `url(${backgroundImage}) !important`
        : `linear-gradient(-45deg, ${backgroundColor}, ${darken(
            backgroundColor,
            0.3
          )}) !important`,
      backgroundSize: `${backgroundSize || 'cover'} !important`,
      backgroundRepeat: 'no-repeat !important',
      backgroundPosition: 'center center !important',
      ...override.aspect,
    },
    content: {
      ...override.content,
    },
  };
};

/**
 * Card Node description...
 */
export const Render = ({
  node: {
    id,
    content: {
      text = '',
      link,
      icon,
      textColor = '#ffffff',
      backgroundImage,
      backgroundColor = '#000000',
      backgroundSize,
      maxWidth = '4',
      minWidth = '1',
      visibleForTicket = '',
    },
    override,
  },
  conferenceId,
}: IProps) => {
  const userHasOneOfTheTickets = userHasTicket(visibleForTicket);
  const nodeProps = useNodeProps(id);
  const { linkActionCall } = useLinkAction();
  const theme = useTheme();
  const rowContext = useRowContext();

  const getColor = (color: string | RGBColor): string =>
    typeof color === 'object' ? rgbToCssColor(color as any) : color;

  const hasLink = (link: ITextLink): boolean =>
    'targetUrl' in link && link.targetUrl !== '';

  const classes = useStyles(theme, {
    maxWidth,
    minWidth,
    backgroundImage: getImage(backgroundImage, ImageSize.Medium, conferenceId),
    backgroundColor: getColor(backgroundColor),
    backgroundSize,
    spacing: 0.5,
    override,
    rowSpacing: rowContext?.horizontalSpacing || 0,
  });

  if (!userHasOneOfTheTickets) return null;

  const handleCardClick = (e: React.MouseEvent) => {
    if (hasLink(link)) {
      const linkProps = linkActionCall(
        {
          target: link.target,
          url: link.targetUrl,
        },
        'href'
      );

      // If it's an action link, trigger the onClick handler
      if (linkProps.onClick) {
        linkProps.onClick(e);
      }
      // If it's a regular URL link
      else if ('href' in linkProps && linkProps.href) {
        e.preventDefault();
        if (link.target === '_blank') {
          window.open(linkProps.href, '_blank', link.targetRel);
        } else {
          window.location.href = linkProps.href;
        }
      }
    }
  };

  return (
    <Box
      sx={{
        ...classes.root,
        ...(hasLink(link) && { cursor: 'pointer' }),
      }}
      {...nodeProps}
      onClick={hasLink(link) ? handleCardClick : undefined}
    >
      <AspectRatio
        maxHeight="40%"
        styles={{
          aspectContainer: {
            ...classes.aspectContainer,
          },
        }}
      >
        <ConditionalRenderer
          condition={hasLink(link)}
          render={(children: ReactNode) => (
            <a
              rel={link.targetRel}
              style={{
                textDecoration: 'none',
                display: 'block',
                cursor: 'pointer',
              }}
              onClick={(e) => e.stopPropagation()}
              {...linkActionCall(
                {
                  target: link.target,
                  url: link.targetUrl,
                },
                'href'
              )}
            >
              {children}
            </a>
          )}
        >
          <CardContent
            contentJustification="center"
            color={getColor(textColor)}
            override={override.content}
          >
            {icon && (
              <Icon
                fontSize="large"
                style={{ display: 'block', margin: '0 auto' }}
              >
                {icon}
              </Icon>
            )}
            <Typography variant="body1" align="center">
              <strong>{text}</strong>
            </Typography>
          </CardContent>
        </ConditionalRenderer>
      </AspectRatio>
    </Box>
  );
};

Render.displayName = 'CardRender';
